import React, { useEffect, useState } from "react";
// Removed unused Dialog imports
import { styled } from "@mui/material";
import { api } from "api/API";
import appStore from "app/mobxStore/AppStore";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Icon,
  IconButton,
  Modal,
  TextField,
  Typography,
  Card,
  Chip,
  Divider,
  InputAdornment,
  Stack,
} from "@mui/material";
import { observer } from "mobx-react";
import { toJS } from "mobx";
interface Entity {
  entityId: number;
  entityName: string;
  entityLocation: string;
  entityPhone: string;
  entityDescription: string;
}

interface EntityDialogProps {
  onClose: () => void;
}

const EntityTile = styled(Card)<{ selected?: boolean }>(
  ({ theme, selected }) => ({
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: "auto",
    padding: theme.spacing(1.5, 2),
    cursor: "pointer",
    border: `1px solid ${
      selected ? theme.palette.primary.main : theme.palette.divider
    }`,
    backgroundColor: selected
      ? theme.palette.action.selected
      : theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: selected ? theme.shadows[2] : "none",
    transition: theme.transitions.create(
      ["transform", "box-shadow", "background-color", "border-color"],
      { duration: theme.transitions.duration.shortest }
    ),
    overflow: "hidden",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[3],
      backgroundColor: theme.palette.action.hover,
    },
  })
);

const ModalContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  width: "min(860px, 96vw)",
  height: "auto",
  maxHeight: "80vh",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[6],
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
}));

const HeroSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderTopLeftRadius: theme.spacing(2),
  borderTopRightRadius: theme.spacing(2),
}));

const EntityDialog: React.FC<EntityDialogProps> = observer(({ onClose }) => {
  const [entities, setEntities] = useState<Entity[]>([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    setOpen(appStore.showEntityDialog);
  }, [`${appStore.showEntityDialog}`]);

  useEffect(() => {
    if (!appStore.loginResponse.success) return;
    if (appStore.userEntities.length > 0) {
      setEntities(toJS(appStore.userEntities));
      return;
    }

    api
      .get(`get-user-entities/${appStore.loginResponse.user[0].userId}`)
      .then((res) => {
        if (res.success) {
          if (res.data.length > 1) {
            setOpen(false);
            // const selectedEntity = res.data[0]?.entityId;
            // appStore.setSelectedEntity(selectedEntity);
          }
          const entities = res.data ?? [];
          appStore.setUserEntities(entities);
          setEntities(entities);
        }
      })
      .catch((err) => {
        throw err;
      });
  }, [appStore.loginResponse.success]);

  const filteredEntities = entities.filter((entity) =>
    entity.entityName.toLowerCase().includes(filter.toLowerCase())
  );
  const columnCount = filteredEntities.length >= 5 ? 3 : 2;
  const totalEntities = entities.length;
  const matchedEntities = filteredEntities.length;

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          appStore.setShowEntityDialog(false);
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(15,23,42,0.65)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <ModalContent>
          <HeroSection>
            <Stack spacing={1}>
              <Typography
                id="entity-dialog-title"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.25rem",
                  color: "text.primary",
                }}
              >
                Select an Entity
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose which workspace you want to work in today. Your
                dashboards, tasks, and permissions are loaded from the selected
                entity.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  label={`${totalEntities} total entities`}
                  size="small"
                  sx={{
                    color: "text.primary",
                    borderColor: "divider",
                    backgroundColor: "background.default",
                  }}
                  variant="outlined"
                />
                <Chip
                  label={`${matchedEntities} visible`}
                  size="small"
                  sx={{
                    color: "text.primary",
                    borderColor: "divider",
                    backgroundColor: "background.default",
                  }}
                  variant="outlined"
                />
              </Stack>
            </Stack>
            <IconButton
              onClick={() => {
                appStore.setShowEntityDialog(false);
                setOpen(false);
              }}
              sx={{
                color: "text.secondary",
                backgroundColor: "background.default",
                border: (theme) => `1px solid ${theme.palette.divider}`,
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </HeroSection>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              px: 3,
              py: 2.5,
              backgroundColor: "background.paper",
            }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                variant="outlined"
                placeholder="Search by entity name..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
            <Divider />
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              overflowX: "hidden",
              px: 3,
              pb: 3,
              display: "grid",
              gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
              gap: 1.5,
            }}
          >
            {filteredEntities.length ? (
              filteredEntities.map((entity) => (
                <EntityTile
                  key={entity.entityId}
                  selected={entity.entityId === appStore.selectedEntity}
                  onClick={() => {
                    appStore.setSelectedEntity(entity.entityId);
                    appStore.setShowEntityDialog(false);
                    setOpen(false);
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      minWidth: 0,
                      width: "100%",
                    }}
                  >
                    <Icon
                      sx={{
                        color: (t) => t.palette.primary.main,
                        fontSize: 22,
                      }}
                    >
                      business
                    </Icon>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        title={entity.entityName}
                        sx={{
                          minWidth: 0,
                          flex: 1,
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          lineHeight: 1.3,
                          maxHeight: "2.6em",
                        }}
                      >
                        {entity.entityName}
                      </Typography>
                      {entity.entityLocation && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          {entity.entityLocation}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {entity.entityId === appStore.selectedEntity && (
                    <Chip
                      size="small"
                      label="Current"
                      color="primary"
                      sx={{ ml: 1 }}
                    />
                  )}
                </EntityTile>
              ))
            ) : (
              <Box
                sx={{
                  gridColumn: `span ${columnCount}`,
                  textAlign: "center",
                  py: 6,
                  border: (t) => `1px dashed ${t.palette.divider}`,
                  borderRadius: 2,
                }}
              >
                <Typography fontWeight={600} gutterBottom>
                  No entities match "{filter}"
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your search or clear the filter to see
                  everything.
                </Typography>
              </Box>
            )}
          </Box>
        </ModalContent>
      </Modal>
    </>
  );
});

export default EntityDialog;
